import type { Router } from 'vue-router'

import { defineStore } from 'pinia'
import { ref } from 'vue'

// 导入类型定义
import type { TeamInitAppResponse } from '@/services/api/user.api'
import type {
  ServerTime,
  Team,
  Token,
  User,
} from '@/types/user'

// 导入 API hooks
import { useAccessControl } from '@/composables/use-access-control'
import { fetchAccessConfig } from '@/services/api/access.api'
import { createShopifySubscriptionLink } from '@/services/api/shopify.api'
import {
  teamSwitchApi,
  useCurrentAttributionModel,
  useTeamInitApp,
} from '@/services/api/user.api'

// 导入服务
import { setIntercomSettings } from '../services/intercom'
import { useAuthStore } from './auth'

export const useUserStore = defineStore('user', () => {
  const authStore = useAuthStore()
  const isAppInitialized = ref(false)
  const isInitializing = ref(false)

  // 添加切换状态锁，防止重复切换
  let switching = false

  // 重置切换状态的方法
  const resetSwitchingState = () => {
    switching = false
  }

  // State - 只保留必要的状态
  const user = ref<Partial<User>>({})
  const token = ref<Token>({
    accessToken: '',
    refreshToken: '',
    refresh_token_valid_time: 0,
  })

  // 保存用户信息到 localStorage（模拟老系统的持久化存储）
  const saveUserToLocalStorage = (userInfo: Partial<User>, teamId: number) => {
    try {
      const vuexKey = `app-shopify-vuex_${teamId}`

      // 先尝试读取现有的数据
      let existingData: any = {}
      const existingDataStr = localStorage.getItem(vuexKey)
      if (existingDataStr) {
        try {
          existingData = JSON.parse(existingDataStr)
        }
        catch (e) {
          console.warn('解析现有数据失败，将创建新数据:', e)
          existingData = {}
        }
      }

      // 创建老系统兼容的数据结构
      const legacyUserData = {
        localCollapse: existingData.localCollapse || {
          all_chart: true,
          ads_chart: true,
          creator_chart: true,
          ads_tracking_error: true,
          creative_chart: true,
        },
        localDefaultViewDataJson: existingData.localDefaultViewDataJson || {
          all: null,
          ads: null,
          home: null,
          live: null,
          creative: null,
          path: null,
          influencer: null,
          product_analytics: null,
        },
        localDefaultViewDataJsons: existingData.localDefaultViewDataJsons || {},
        leadsListPage: existingData.leadsListPage || 1,
        email: existingData.email || {},
        user: {
          user: {
            // 包含其他可能的用户字段，但排除 team_list
            ...userInfo,
            // 然后明确设置核心字段，确保不被覆盖
            id: userInfo.id,
            name: userInfo.name,
            team_id: userInfo.team_id,
            hash: userInfo.hash || '',
            email: userInfo.email,
            administer_flag: userInfo.administer_flag || false,
            source: userInfo.source || 0,
            login_from: userInfo.login_from,
            team: userInfo.team,
            team_list: userInfo.team_list || existingData.user?.user?.team_list, // 保持原有的 team_list
            team_name: userInfo.team_name,
            team_timezone: userInfo.team_timezone,
            server_time: userInfo.server_time,
            onborading: userInfo.onborading,
            onboardingGuide: userInfo.onboardingGuide,
            event_type_v4: userInfo.event_type_v4,
            shopify_price: userInfo.shopify_price,
            sub_items_price: userInfo.sub_items_price,
            sendEmailInfo: userInfo.sendEmailInfo,
            // 确保保存任务相关数据
            taskComplete: userInfo.taskComplete,
            taskCompleteProgress: userInfo.taskCompleteProgress,
          },
          cosCredential: existingData.user?.cosCredential || null,
        },
        automation: existingData.automation || {},
      }

      // 保存合并后的数据
      localStorage.setItem(vuexKey, JSON.stringify(legacyUserData))
      console.warn('老系统兼容数据已保存到 localStorage:', vuexKey)
    }
    catch (e) {
      console.warn('保存用户信息到 localStorage 失败:', e)
    }
  }

  // 保存用户基本信息到 sessionStorage 和 localStorage
  const saveUserBasicInfo = (userInfo: Partial<User>, token?: string) => {
    if (userInfo.id) {
      sessionStorage.setItem('user_id', String(userInfo.id))
    }
    if (userInfo.name) {
      sessionStorage.setItem('user_name', userInfo.name)
    }
    if (userInfo.email) {
      sessionStorage.setItem('user_email', userInfo.email)
    }
    if (userInfo.hash) {
      sessionStorage.setItem('user_hash', userInfo.hash)
    }
    if (userInfo.administer_flag !== undefined) {
      sessionStorage.setItem('user_administer_flag', String(userInfo.administer_flag))
    }
    if (userInfo.team_id) {
      sessionStorage.setItem('team_id', String(userInfo.team_id))
    }
    if (userInfo.team) {
      sessionStorage.setItem('team', JSON.stringify(userInfo.team))
      sessionStorage.setItem('team_code', userInfo.team.team_code || '')
      // 同时保存 team_name 和 team_timezone
      if (userInfo.team.name) {
        sessionStorage.setItem('team_name', userInfo.team.name)
      }
      if (userInfo.team.timezone) {
        sessionStorage.setItem('team_timezone', userInfo.team.timezone)
      }
    }
    if (userInfo.team_list && userInfo.team_list.length > 0) {
      sessionStorage.setItem('teams', JSON.stringify(userInfo.team_list))
    }
    if (token) {
      sessionStorage.setItem('token', token)
    }
  }

  // Actions - 只保留与initApp和switchTeam相关的操作
  const setUserInfo = (userInfo: Partial<User> & { bill_sub_items?: any }) => {
    // 处理 API 返回的字段映射：bill_sub_items -> sub_items_price（同时保留两个字段）
    if ('bill_sub_items' in userInfo && userInfo.bill_sub_items) {
      userInfo.sub_items_price = userInfo.bill_sub_items
      // delete userInfo.bill_sub_items
    }

    // 确保团队有默认模型
    if (userInfo.team && !userInfo.team.default_model) {
      userInfo.team.default_model = '1cwm0j21fmwcyfwu07m624g100phdcbl'
    }

    // 确保团队列表中的每个团队都有默认模型
    if (userInfo.team_list) {
      userInfo.team_list = userInfo.team_list.map((team) => {
        if (!team.default_model) {
          team.default_model = '1cwm0j21fmwcyfwu07m624g100phdcbl'
        }
        return team
      })
    }

    user.value = { ...user.value, ...userInfo }

    // 强制修改 Plan (用户请求)
    if (import.meta.env.VITE_FORCE_PLAN) {
      const forcePlan = (import.meta.env.VITE_FORCE_PLAN as string).toLowerCase()
      // 确保对象结构存在
      if (!user.value.sub_items_price) {
        user.value.sub_items_price = {} as any
      }
      if (!user.value.sub_items_price!.plan) {
        user.value.sub_items_price!.plan = {} as any
      }
      user.value.sub_items_price!.plan.item_plan = forcePlan
    }
    if (import.meta.env.VITE_FORCE_TERM && user.value.sub_items_price?.plan) {
      user.value.sub_items_price!.plan.item_term = import.meta.env.VITE_FORCE_TERM as string
    }
    if (import.meta.env.VITE_FORCE_RESIDUAL_TRIAL_DAYS !== undefined && user.value.sub_items_price?.plan) {
      const days = Number(import.meta.env.VITE_FORCE_RESIDUAL_TRIAL_DAYS)
      if (!Number.isNaN(days)) {
        user.value.sub_items_price!.plan.residual_trial_days = days
      }
    }

    // 开发环境临时覆盖计划（支持通过环境变量控制）
    // 仅在“当前店铺”生效：记录首次覆盖时的团队ID，后续仅当团队ID相同才覆盖

    // 初始化 Intercom（如果用户信息完整且有登录来源和hash）
    if (userInfo.id) {
      setIntercomSettings(user.value)
    }

    // 如果有团队ID，保存用户信息到 localStorage
    // if (userInfo.team_id) {
    //   saveUserToLocalStorage(user.value, userInfo.team_id)
    // }
  }

  const setDefaultModel = (defaultModel: string) => {
    if (user.value.team) {
      user.value.team.default_model = defaultModel
      // 更新teamlist中的default_model
      if (user.value.team_list) {
        user.value.team_list = user.value.team_list.map((e) => {
          if (e.id === user.value.team?.id) {
            e.default_model = defaultModel
          }
          return e
        })
      }

      // 保存到 localStorage，确保刷新后能恢复正确的默认模型
      if (user.value.team_id) {
        saveUserToLocalStorage(user.value, user.value.team_id)
      }

      // 更新 sessionStorage 中的 team 信息
      if (user.value.team) {
        sessionStorage.setItem('team', JSON.stringify(user.value.team))
      }
    }
  }

  // 设置 token 的辅助方法
  const setToken = (newToken: string) => {
    token.value.accessToken = newToken
    // 可以根据需要设置过期时间等其他 token 信息
  }

  // 从 localStorage 恢复用户完整信息的方法
  const restoreUserFromLocalStorage = (teamId: number): Partial<User> | null => {
    try {
      const vuexKey = `app-shopify-vuex_${teamId}`
      const vuexData = localStorage.getItem(vuexKey)

      if (vuexData) {
        const parsedData = JSON.parse(vuexData)
        if (parsedData.user?.user) {
          const userData = parsedData.user.user
          return {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            team_id: userData.team_id,
            hash: userData.hash,
            administer_flag: userData.administer_flag,
            source: userData.source,
            login_from: userData.login_from,
            team: userData.team,
            team_list: userData.team_list,
            team_name: userData.team_name,
            team_timezone: userData.team_timezone,
            server_time: userData.server_time,
            onborading: userData.onborading,
            onboardingGuide: userData.onboardingGuide,
            event_type_v4: userData.event_type_v4,
            shopify_price: userData.shopify_price,
            sub_items_price: userData.sub_items_price,
            sendEmailInfo: userData.sendEmailInfo,
            bill_sub_items: userData.bill_sub_items,
            question_complete: userData.question_complete,
            // 添加其他需要的字段
            ...userData,
          }
        }
      }
    }
    catch (e) {
      console.warn('从localStorage恢复用户信息失败:', e)
    }
    return null
  }

  // 检查登录状态的方法
  const checkLoginStatus = (): { hasLogin: boolean, hasTeam: boolean, teamId?: number } => {
    const token = sessionStorage.getItem('token')
    const teamId = sessionStorage.getItem('team_id')

    return {
      hasLogin: !!token,
      hasTeam: !!teamId,
      teamId: teamId ? Number(teamId) : undefined,
    }
  }

  // 获取token和session管理
  const initTokenAndSession = (): { needLogin?: boolean, needSelectStore?: boolean, success?: boolean, user?: Partial<User> } => {
    // 先检查from_operate_data，如果有，则先恢复数据到session
    const fromOperateData = localStorage.getItem('from_operate_data')
    if (fromOperateData) {
      try {
        const parsedData = JSON.parse(fromOperateData)
        if (parsedData) {
          // 恢复用户基本信息到 sessionStorage
          sessionStorage.setItem('token', parsedData.token || '')
          sessionStorage.setItem('user_id', String(parsedData.user_id || ''))
          sessionStorage.setItem('team_id', parsedData.team_id)
          sessionStorage.setItem('team_code', parsedData.team_code || '')
          sessionStorage.setItem('team', JSON.stringify(parsedData.team || {}))
        }
        // 清除已使用的数据，避免重复处理
        localStorage.removeItem('from_operate_data')
      }
      catch (e) {
        console.warn('解析 from_operate_data 失败:', e)
      }
    }

    const loginStatus = checkLoginStatus()

    if (!loginStatus.hasLogin) {
      console.warn('未检测到登录状态')
      return { needLogin: true }
    }

    if (!loginStatus.hasTeam) {
      console.warn('已登录但未选择团队')
      // 尝试从基本session恢复用户信息（不包含团队信息）
      const token = sessionStorage.getItem('token')
      if (token) {
        setToken(token)

        const userId = sessionStorage.getItem('user_id')
        const userEmail = sessionStorage.getItem('user_email')
        const userName = sessionStorage.getItem('user_name')

        let teamList: Team[] = []
        const teamsJson = sessionStorage.getItem('teams')
        if (teamsJson) {
          try {
            teamList = JSON.parse(teamsJson)
          }
          catch (e) {
            console.warn('解析团队列表失败:', e)
          }
        }

        const basicUserInfo: Partial<User> = {}
        if (userId)
          basicUserInfo.id = Number(userId)
        if (userEmail)
          basicUserInfo.email = userEmail
        if (userName)
          basicUserInfo.name = userName
        if (teamList.length > 0)
          basicUserInfo.team_list = teamList

        if (Object.keys(basicUserInfo).length > 0) {
          setUserInfo(basicUserInfo)
          console.warn('从基本信息恢复用户数据成功（无团队）:', basicUserInfo)
        }
      }
      return { needSelectStore: true }
    }

    // 从 app-shopify-vuex_(teamId) 恢复完整的用户信息
    const restoredUser = restoreUserFromLocalStorage(loginStatus.teamId!)
    console.warn('restoreUserFromLocalStorage 返回:', restoredUser)

    if (restoredUser) {
      // 如果 localStorage 中恢复的 team 数据不完整，尝试从 sessionStorage 补充
      if (!restoredUser.team || !restoredUser.team.team_code) {
        console.warn('localStorage 中的 team 数据不完整，restoredUser.team:', restoredUser.team)
        const teamJson = sessionStorage.getItem('team')
        if (teamJson) {
          try {
            const sessionTeam = JSON.parse(teamJson)
            if (sessionTeam && sessionTeam.team_code) {
              console.warn('使用 sessionStorage 的 team 数据:', sessionTeam)
              restoredUser.team = sessionTeam
              restoredUser.team_name = sessionTeam.name
              restoredUser.team_timezone = sessionTeam.timezone
            }
          }
          catch (e) {
            console.warn('解析 sessionStorage team 失败:', e)
          }
        }
      }

      // 设置 token
      const token = sessionStorage.getItem('token')
      if (token) {
        setToken(token)
      }

      // 设置用户信息
      setUserInfo(restoredUser)

      console.warn('从 localStorage 恢复用户数据，最终 user.value.team:', user.value.team)

      return { success: true, user: restoredUser }
    }

    console.warn('localStorage 恢复失败，尝试从 sessionStorage 恢复')

    // 如果无法从localStorage恢复，尝试从基本session恢复
    const token = sessionStorage.getItem('token')
    if (token) {
      setToken(token)

      const userId = sessionStorage.getItem('user_id')
      const userEmail = sessionStorage.getItem('user_email')
      const userName = sessionStorage.getItem('user_name')
      const userHash = sessionStorage.getItem('user_hash')
      const userAdministerFlag = sessionStorage.getItem('user_administer_flag')
      const teamId = sessionStorage.getItem('team_id')

      let currentTeam: Team | undefined
      const teamJson = sessionStorage.getItem('team')
      if (teamJson) {
        try {
          currentTeam = JSON.parse(teamJson)
        }
        catch (e) {
          console.warn('解析团队信息失败:', e)
        }
      }

      let teamList: Team[] = []
      const teamsJson = sessionStorage.getItem('teams')
      if (teamsJson) {
        try {
          teamList = JSON.parse(teamsJson)
        }
        catch (e) {
          console.warn('解析团队列表失败:', e)
        }
      }

      const basicUserInfo: Partial<User> = {}
      if (userId)
        basicUserInfo.id = Number(userId)
      if (userEmail)
        basicUserInfo.email = userEmail
      if (userName)
        basicUserInfo.name = userName
      if (userHash)
        basicUserInfo.hash = userHash
      if (userAdministerFlag !== null)
        basicUserInfo.administer_flag = userAdministerFlag === 'true'
      if (teamId)
        basicUserInfo.team_id = Number(teamId)
      if (currentTeam) {
        basicUserInfo.team = currentTeam
        basicUserInfo.team_name = currentTeam.name
        basicUserInfo.team_timezone = currentTeam.timezone
      }
      if (teamList.length > 0)
        basicUserInfo.team_list = teamList

      if (Object.keys(basicUserInfo).length > 0) {
        setUserInfo(basicUserInfo)
        console.warn('从基本信息恢复用户数据成功:', basicUserInfo)
        console.warn('恢复后的 user.value.team:', user.value.team)
        return { success: true, user: basicUserInfo }
      }
    }

    return { needLogin: true }
  }

  // 团队切换
  const switchTeam = async (item: Team & { click_source?: string, redirect?: string }, router?: Router) => {
    try {
      if (item.click_source !== 'login') {
        // 这里可以添加确认对话框逻辑
        // const confirmed = window.confirm(`Are you sure you want to switch to ${item.name}?`)
        // if (!confirmed) return
      }

      switching = true

      const requestData = { team_id: item.id }

      // 使用 API 函数进行团队切换
      const res = await teamSwitchApi(requestData)

      sessionStorage.removeItem('hideNetMarginAlert')
      sessionStorage.removeItem('attribuly_init_app_cache')

      // 使用sessionStorage存储token，team_code，team_id
      sessionStorage.setItem('token', res.token || '')

      // 更新 store 中的 token
      setToken(res.token || '')

      // 设置cookie以支持跨域
      const expires = new Date(new Date().getTime() + 31536e6).toUTCString()
      document.cookie = `_attribuly_token=${res.token}; domain=.attribuly.com; path=/; expires=${expires}; SameSite=None; Secure`
      document.cookie = `_attribuly_team=${JSON.stringify(item)}; domain=.attribuly.com; path=/; expires=${expires}; SameSite=None; Secure`

      // 更新用户信息，确保包含完整的团队数据，但保持原有的 team_list
      const updatedUserInfo: Partial<User> = {
        ...user.value,
        ...res.user,
        team_id: item.id,
        team: item,
        team_name: item.name,
        team_timezone: item.timezone,
        team_list: user.value.team_list, // 保持原有的 team_list，不被覆盖
        login_from: user.value.login_from || undefined, // 保持 login_from 标记
      }

      setUserInfo(updatedUserInfo)

      // 保存用户基本信息到存储
      saveUserBasicInfo(updatedUserInfo, res.token)

      // 保存用户信息到 localStorage（模拟老系统的持久化存储）
      saveUserToLocalStorage(updatedUserInfo, item.id)

      // 重新加载页面
      if (item.redirect) {
        console.warn('团队切换后跳转到指定路径:', item.redirect)
        // 如果传入了 router 实例，使用 Vue Router 进行导航
        if (router) {
          try {
            await router.push(item.redirect)
            return
          }
          catch (err) {
            console.warn('Router navigation failed, falling back to location.href:', err)
          }
        }
        // 降级方案：使用 window.location.href
        window.location.href = item.redirect
        return
      }

      window.location.reload()
    }
    catch (e) {
      console.error('团队切换失败:', e)
      // 确保无论如何都要重置状态
      switching = false
      // 抛出错误，让调用方能够处理
      throw e
    }
  }

  const getQuickProAnnualSubscriptionLink = async (): Promise<string | null> => {
    try {
      const response = await createShopifySubscriptionLink({
        sub_items: 'pro',
        sub_pro: {
          type: 'year',
        },
      })

      const payUrl = response?.data?.pay_url ?? null
      if (payUrl) {
        window.open(payUrl, '_blank', 'noopener')
      }

      return payUrl
    }
    catch (error) {
      console.error('获取订阅链接失败:', error)
      throw error
    }
  }

  // 计算任务完成进度 - 参考老系统的逻辑
  const calculateTaskCompleteProgress = (taskList: any[] = []): number => {
    if (!taskList || taskList.length === 0) {
      return 0
    }

    const completedTasks = taskList.filter(task => task.is_finished === true)
    const progress = Math.round((completedTasks.length / taskList.length) * 100)
    return progress
  }

  // 更新任务完成进度到用户信息和localStorage
  const updateTaskCompleteProgress = (progress: number) => {
    // 更新用户信息中的进度
    user.value.taskCompleteProgress = progress

    // 使用完整的保存函数来确保数据结构正确
    if (user.value.team_id) {
      saveUserToLocalStorage(user.value, user.value.team_id)
    }
  }

  // 初始化 Onboarding 状态
  // 返回值：true 表示任务已初始化（不需要跳转到 onboarding 问卷），false 表示需要跳转到 onboarding 问卷
  // 抛出错误：接口请求失败时抛出错误，由调用方处理
  //
  // loadFromBackend 返回值说明：
  //   'new' - 新用户，需要走新版 onboarding 问卷流程
  //   'questionnaire' - 问卷正在进行中，需要恢复到问卷页面的某个问题
  //   'legacy' - 老用户使用 v7 tasks，显示 floating guide
  //   'progress' - 新版有进度，需要继续未完成的步骤
  //   'completed' - 已完成所有 onboarding，不显示任何引导
  //
  // 返回值说明：
  //   { needOnboarding: false } - 不需要跳转，用户可以继续访问目标页面
  //   { needOnboarding: true, redirectTo: '/onboarding' } - 需要跳转到问卷页面（新用户）
  //   { needOnboarding: true, redirectTo: '/onboarding/xxx' } - 需要跳转到特定步骤（有进度的用户）
  const initOnboarding = async (): Promise<{ needOnboarding: boolean, redirectTo?: string }> => {
    try {
      const { useOnboardingSetupStore } = await import('@/stores/onboarding-setup')
      const setupStore = useOnboardingSetupStore()
      const result = await setupStore.loadFromBackend()

      const computeRetentionRedirect = async (): Promise<{ needOnboarding: boolean, redirectTo?: string }> => {
        const {
          onboardingProgressGetApi,
        } = await import('@/services/api/platform.api')
        const {
          useOnboardingRetentionProgressStore,
        } = await import('@/stores/onboarding-retention-progress')
        const retentionData = await onboardingProgressGetApi({ version: 'retention-v1' })
        const retentionStore = useOnboardingRetentionProgressStore()
        if (retentionData) {
          retentionStore.progress = retentionData
          if (!retentionStore.selectedEsp) {
            const espFromData = (retentionData.progress_data || {}).selectedEsp as any
            if (espFromData === 'klaviyo' || espFromData === 'omnisend' || espFromData === 'cartsee')
              retentionStore.selectedEsp = espFromData
          }
        }
        if (retentionData?.is_completed)
          return { needOnboarding: false }
        const resumePath = retentionStore.getResumePath() || '/onboarding-retention/step0'
        return { needOnboarding: true, redirectTo: resumePath }
      }

      switch (result) {
        case 'new':
          // 新用户，需要跳转到 /onboarding 问卷页面
          console.warn('新用户，需要跳转到 /onboarding 完成问卷')
          return computeRetentionRedirect()

        case 'questionnaire':
          // 问卷正在进行中，需要恢复到问卷页面继续
          console.warn('问卷进行中，需要跳转到 /onboarding 继续问卷')
          return computeRetentionRedirect()

        case 'legacy':
          // 老用户使用 v7 tasks，显示 floating guide，不需要跳转
          console.warn('老用户使用 v7 tasks 版本，显示 floating guide')
          return { needOnboarding: false }

        case 'progress':
          // 新版有进度但未完成，需要重定向到恢复的步骤页面
          console.warn('新版 onboarding 有进度但未完成，需要继续步骤')
          // 根据 currentStep 计算需要跳转的页面
          const redirectPath = setupStore.getResumeStepPath()
          if (redirectPath) {
            return { needOnboarding: true, redirectTo: redirectPath }
          }
          // 如果没有可恢复的步骤，返回问卷页面
          return { needOnboarding: true, redirectTo: '/onboarding' }

        case 'completed':
          // 已完成所有 onboarding
          console.warn('用户已完成所有 onboarding')
          return { needOnboarding: false }

        default:
          console.warn('未知的 onboarding 状态:', result)
          return { needOnboarding: true, redirectTo: '/onboarding' }
      }
    }
    catch (error) {
      console.error('Failed to initialize onboarding:', error)
      // 抛出错误，让调用方（路由守卫）处理
      throw error
    }
  }

  // 应用初始化
  // 返回值包含：
  //   data - 用户数据
  //   needOnboarding - 是否需要跳转到 onboarding
  //   onboardingRedirectTo - 需要跳转的目标路径（可选）
  const initApp = async (): Promise<{ data?: TeamInitAppResponse, needOnboarding: boolean, onboardingRedirectTo?: string }> => {
    // 检查缓存，如果有缓存直接返回缓存数据
    isAppInitialized.value = true

    // Check if current URL contains both contract_id and billing_intent_id strings
    const currentUrl = window.location.href
    const hasContractId = currentUrl.includes('contract_id')
    const hasBillingIntentId = currentUrl.includes('billing_intent_id')

    // If both strings are present in the URL, delete the session storage key after 3 seconds delay
    if (hasContractId && hasBillingIntentId) {
      console.log('URL contains both contract_id and billing_intent_id, removing cache after 3 seconds')
      try {
        setTimeout(() => {
          sessionStorage.removeItem('attribuly_init_app_cache')
          console.log('Removed attribuly_init_app_cache from sessionStorage after 3 seconds delay')
        }, 3000)
      }
      catch (e) {
        console.error('Error removing attribuly_init_app_cache from sessionStorage:', e)
      }
    }

    let cachedData = sessionStorage.getItem('attribuly_init_app_cache')
    // 登录态变化后（如 shopify-session 重新登录、切换店铺）旧缓存已失效，强制重新请求 init 接口
    if (cachedData) {
      try {
        const parsedCache = JSON.parse(cachedData)
        const cacheToken = parsedCache?.token || ''
        const currentToken = token.value.accessToken || sessionStorage.getItem('token') || ''
        if (cacheToken !== currentToken) {
          console.warn('initApp 缓存对应的登录态已失效，重新请求 init 接口')
          sessionStorage.removeItem('attribuly_init_app_cache')
          cachedData = null
        }
      }
      catch (e) {
        console.warn('解析 initApp 缓存 token 失败:', e)
      }
    }
    if (cachedData) {
      try {
        const parsedCache = JSON.parse(cachedData)
        console.warn('使用 initApp 缓存数据')
        const data = parsedCache.data

        // 设置默认模型
        if (data.bill_sub_items?.default_model) {
          setDefaultModel(data.bill_sub_items.default_model)
        }

        // 确保 data.team 中的 default_model 与 bill_sub_items 一致，防止 setUserInfo 覆盖
        if (data.team && data.bill_sub_items?.default_model) {
          data.team.default_model = data.bill_sub_items.default_model
        }

        // 更新用户信息
        setUserInfo(data)

        // 设置权限数据
        if (data.team_auth?.auth_map) {
          const authMap = data.team_auth.auth_map

          // 强制设置 attribution_model 和 channel_group 为企业版功能
          // 如果用户不是企业版，则标记为需要升级
          const itemPlan = String(data.bill_sub_items?.plan?.item_plan || '').toLowerCase()
          const enterprisePlan = String(data.bill_sub_items?.plan?.item_billing_plan_type || '').toLowerCase()
          const isEnterprise = enterprisePlan.includes('enterprise') || enterprisePlan.includes('recapture')
          const isStarter = itemPlan === 'starter' || itemPlan === 'free'
          const isPro = itemPlan.includes('pro') || itemPlan.includes('premium')

          if (!authMap.attribution_model) {
            authMap.attribution_model = {
              available: isEnterprise ? '' : 'enterprise',
              limited: !isEnterprise,
            }
          }

          if (!authMap.channel_group) {
            authMap.channel_group = {
              available: isEnterprise ? '' : 'enterprise',
              limited: !isEnterprise,
            }
          }

          if (isStarter) {
            authMap.attribution_model = {
              ...(authMap.attribution_model || {}),
              available: '',
              limited: false,
            }
            authMap.channel_group = {
              ...(authMap.channel_group || {}),
              available: '',
              limited: false,
            }
            authMap.api = {
              ...(authMap.api || {}),
              available: '',
              limited: false,
            }
            authMap.custom_domain = {
              ...(authMap.custom_domain || {}),
              available: '',
              limited: false,
            }
          }
          if (isPro) {
            authMap.attribution_model = {
              ...(authMap.attribution_model || {}),
              available: '',
              limited: false,
            }
            authMap.channel_group = {
              ...(authMap.channel_group || {}),
              available: '',
              limited: false,
            }
            authMap.api = {
              ...(authMap.api || {}),
              available: '',
              limited: false,
            }
          }

          authStore.setAuthMap(authMap)
        }

        // 保存用户基本信息
        const currentToken = token.value.accessToken || sessionStorage.getItem('token')
        if (currentToken && user.value.team_id) {
          saveUserBasicInfo(data, currentToken)
          // 保存老系统兼容数据
          saveUserToLocalStorage({ ...user.value, ...data }, user.value.team_id)
        }

        // 获取并设置页面访问权限配置（缓存分支也需要刷新权限）
        const currentUserId = user.value.id
        if (currentUserId) {
          try {
            const accessConfigData = await fetchAccessConfig(currentUserId)
            if (accessConfigData?.access) {
              const { setAccessConfig } = useAccessControl()
              setAccessConfig(accessConfigData.access)
              console.warn('页面访问权限配置已加载（缓存分支）:', accessConfigData.access)
            }
          }
          catch (error) {
            console.warn('获取页面访问权限配置失败（缓存分支）:', error)
            // 权限配置获取失败不影响初始化流程
          }
        }

        // 初始化 Onboarding 状态（使用缓存数据时也需要初始化）
        // 如果接口出错，捕获错误并标记为不需要 onboarding（让路由守卫处理）
        let onboardingResult: { needOnboarding: boolean, redirectTo?: string } = { needOnboarding: false }
        try {
          onboardingResult = await initOnboarding()
        }
        catch (error) {
          // 接口出错时，抛出错误让路由守卫处理
          console.error('初始化 onboarding 状态失败（缓存分支）:', error)
          throw error
        }

        return { data, needOnboarding: onboardingResult.needOnboarding, onboardingRedirectTo: onboardingResult.redirectTo }
      }
      catch (error) {
        console.warn('解析 initApp 缓存数据失败:', error)
      }
    }

    console.warn('发起 initApp 接口请求')

    const teamInitAppMutation = useTeamInitApp()
    const data = await teamInitAppMutation.mutateAsync()

    console.warn('initApp 接口响应数据:', JSON.stringify(data))

    if (!data) {
      console.error('应用初始化失败，未获取到用户信息')
      return { needOnboarding: false, onboardingRedirectTo: undefined }
    }

    // 缓存数据到 sessionStorage
    console.warn('缓存 initApp 数据到 sessionStorage', data)
    if (data?.bill_sub_items?.plan?.item_plan) {
      try {
        const cacheData = {
          token: token.value.accessToken || sessionStorage.getItem('token') || '',
          data,
        }
        sessionStorage.setItem('attribuly_init_app_cache', JSON.stringify(cacheData))
        console.warn('initApp 数据已缓存1', JSON.stringify(cacheData))
      }
      catch (error) {
        console.warn('缓存 initApp 数据失败:', error)
      }
    }

    // 如果响应中有 bill_sub_items.default_model，设置默认模型
    if (data.bill_sub_items?.default_model) {
      setDefaultModel(data.bill_sub_items.default_model)
    }

    // 确保 data.team 中的 default_model 与 bill_sub_items 一致，防止 setUserInfo 覆盖
    if (data.team && data.bill_sub_items?.default_model) {
      data.team.default_model = data.bill_sub_items.default_model
    }

    // 直接使用响应数据更新用户信息，因为 API 返回的就是完整的用户数据
    setUserInfo(data)

    // 如果 API 返回了任务完成数据，计算并更新进度
    if (data.taskComplete && Array.isArray(data.taskComplete)) {
      const progress = calculateTaskCompleteProgress(data.taskComplete)
      updateTaskCompleteProgress(progress)
    }

    // 设置权限数据
    if (data.team_auth?.auth_map) {
      const authMap = data.team_auth.auth_map

      // 强制设置 attribution_model 和 channel_group 为企业版功能
      // 如果用户不是企业版，则标记为需要升级
      const itemPlan = String(data.bill_sub_items?.plan?.item_plan || '').toLowerCase()
      // 与缓存分支及代码库其他位置（getSegmentsPath 等）保持一致：使用 item_billing_plan_type 判定企业版
      const enterprisePlan = String(data.bill_sub_items?.plan?.item_billing_plan_type || '').toLowerCase()
      const isEnterprise = enterprisePlan.includes('enterprise') || enterprisePlan.includes('recapture')
      const isStarter = itemPlan === 'starter' || itemPlan === 'free'
      const isPro = itemPlan.includes('pro') || itemPlan.includes('premium')

      if (!authMap.attribution_model) {
        authMap.attribution_model = {
          available: isEnterprise ? '' : 'enterprise',
          limited: !isEnterprise,
        }
      }

      if (!authMap.channel_group) {
        authMap.channel_group = {
          available: isEnterprise ? '' : 'enterprise',
          limited: !isEnterprise,
        }
      }

      if (isStarter) {
        authMap.attribution_model = {
          ...(authMap.attribution_model || {}),
          available: '',
          limited: false,
        }
        authMap.channel_group = {
          ...(authMap.channel_group || {}),
          available: '',
          limited: false,
        }
        authMap.api = {
          ...(authMap.api || {}),
          available: '',
          limited: false,
        }
        authMap.custom_domain = {
          ...(authMap.custom_domain || {}),
          available: '',
          limited: false,
        }
      }
      if (isPro) {
        authMap.attribution_model = {
          ...(authMap.attribution_model || {}),
          available: '',
          limited: false,
        }
        authMap.channel_group = {
          ...(authMap.channel_group || {}),
          available: '',
          limited: false,
        }
        authMap.api = {
          ...(authMap.api || {}),
          available: '',
          limited: false,
        }
      }

      authStore.setAuthMap(authMap)
    }

    // 获取并设置页面访问权限配置
    // 传递当前用户的 user_id
    const currentUserId = user.value.id
    if (currentUserId) {
      try {
        const accessConfigData = await fetchAccessConfig(currentUserId)
        if (accessConfigData?.access) {
          const { setAccessConfig } = useAccessControl()
          setAccessConfig(accessConfigData.access)
          console.warn('页面访问权限配置已加载:', accessConfigData.access)
        }
      }
      catch (error) {
        console.warn('获取页面访问权限配置失败:', error)
        // 权限配置获取失败不影响初始化流程
      }
    }

    // 如果当前有 token，保存用户基本信息
    const currentToken = token.value.accessToken || sessionStorage.getItem('token')
    if (currentToken && user.value.team_id) {
      saveUserBasicInfo(data, currentToken)
      // 保存老系统兼容数据
      saveUserToLocalStorage({ ...user.value, ...data }, user.value.team_id)
    }

    // 初始化 Onboarding 状态（在店铺选择后）
    // 如果接口出错，捕获错误并标记为不需要 onboarding（让路由守卫处理）
    let onboardingResult: { needOnboarding: boolean, redirectTo?: string } = { needOnboarding: false }
    try {
      onboardingResult = await initOnboarding()
    }
    catch (error) {
      // 接口出错时，抛出错误让路由守卫处理
      console.error('初始化 onboarding 状态失败:', error)
      throw error
    }

    return { data, needOnboarding: onboardingResult.needOnboarding, onboardingRedirectTo: onboardingResult.redirectTo }
  }

  // 辅助方法
  const getCurrentTeam = (): Team | undefined => {
    return user.value.team
  }

  const getTeamById = (teamId: number): Team | undefined => {
    return user.value.team_list?.find(team => team.id === teamId)
  }

  const hasAccessFlag = (): boolean => {
    return user.value.sub_items_price?.access_flag === true
  }

  const isOnboardingFinished = (): boolean => {
    return user.value.onboardingGuide?.is_finished === true
  }

  const getServerTime = (): ServerTime | undefined => {
    return user.value.server_time
  }

  const getSubscriptionPlanUrl = (query?: string): string => {
    const subscriptionV1LaunchAt = new Date('2026-05-10T00:00:00Z')
    const subscriptionRetentionLaunchAt = new Date('2026-06-02T09:56:57Z')
    const currentTeam = getCurrentTeam() || getTeamById(Number(sessionStorage.getItem('team_id') || 0))
    const createdAt = currentTeam?.created_at
    const createdAtTime = createdAt ? Date.parse(createdAt) : Number.NaN
    const isNewUser = Number.isFinite(createdAtTime) && createdAtTime >= subscriptionV1LaunchAt.getTime()

    const baseDomain = 'https://attribuly.com'

    const base = Number.isFinite(createdAtTime) && createdAtTime >= subscriptionRetentionLaunchAt.getTime()
      ? `${baseDomain}/select-plan-retention`
      : (isNewUser ? `${baseDomain}/select-plan-v1` : `${baseDomain}/select-plan`)

    if (!query)
      return base

    const normalized = query.startsWith('?') ? query.slice(1) : query
    return `${base}?${normalized}`
  }

  const shouldUseSidebarMultiModulePlanStatusCard = (): boolean => {
    /* const envFlag = String(import.meta.env.VITE_FORCE_SIDEBAR_MULTI_MODULE_PLAN_STATUS || '').trim().toLowerCase()
    if (envFlag) {
      return envFlag === '1' || envFlag === 'true'
    }
    try {
      const flag = localStorage.getItem('force_sidebar_multi_module_plan_status')
      if (flag !== null) {
        return flag === '1'
      }
    }
    catch {
    } */
    return true
    /* const planStatusMultiModuleLaunchAt = new Date('2026-06-01T00:00:00Z')
    const currentTeam = getCurrentTeam() || getTeamById(Number(sessionStorage.getItem('team_id') || 0))
    const createdAt = currentTeam?.created_at
    const createdAtTime = createdAt ? Date.parse(createdAt) : Number.NaN
    return Number.isFinite(createdAtTime) && createdAtTime >= planStatusMultiModuleLaunchAt.getTime() */
  }

  const isForceNewProV1 = (): boolean => {
    try {
      const flag = localStorage.getItem('force_new_pro_v1')
      if (flag === null) {
        return import.meta.env.DEV
      }
      return flag === '1'
    }
    catch {
      return false
    }
  }

  const getBillingPath = (): string => {
    const itemPlanType = String(user.value?.sub_items_price?.plan?.item_plan_type || '').toLowerCase()
    if (itemPlanType === 'recapture' || itemPlanType === 'recapture_free')
      return '/billing-recapture'

    const billingV1LaunchAt = new Date('2026-03-25T00:00:00Z')
    const currentTeam = getCurrentTeam() || getTeamById(Number(sessionStorage.getItem('team_id') || 0))
    const createdAt = currentTeam?.created_at
    const createdAtTime = isForceNewProV1() ? Date.now() : (createdAt ? Date.parse(createdAt) : Number.NaN)
    if (!Number.isFinite(createdAtTime)) {
      return '/billing'
    }
    return createdAtTime >= billingV1LaunchAt.getTime() ? '/billing-v1' : '/billing'
  }

  const getSegmentsPath = (): string => {
    const segmentsV1LaunchAt = new Date('2026-03-25T00:00:00Z')
    const currentTeam = getCurrentTeam() || getTeamById(Number(sessionStorage.getItem('team_id') || 0))
    const createdAt = currentTeam?.created_at
    const createdAtTime = isForceNewProV1() ? Date.now() : (createdAt ? Date.parse(createdAt) : Number.NaN)
    const plan = String(user.value?.sub_items_price?.plan?.item_billing_plan_type || '').toLowerCase()
    const residualTrialDays = user.value?.sub_items_price?.plan?.residual_trial_days || 0
    const isEnterprise = plan.includes('enterprise') || plan.includes('recapture')
    const isPro = isForceNewProV1() && !isEnterprise ? true : (plan.includes('pro') || plan.includes('premium') || residualTrialDays > 0)

    if (!isPro || isEnterprise) {
      return '/store/segments'
    }
    if (!Number.isFinite(createdAtTime)) {
      return '/store/segments'
    }
    return createdAtTime >= segmentsV1LaunchAt.getTime() ? '/store/segments-v1' : '/store/segments'
  }

  const getWebAnalysisPath = (): string => {
    const webAnalysisV1LaunchAt = new Date('2026-03-25T00:00:00Z')
    const currentTeam = getCurrentTeam() || getTeamById(Number(sessionStorage.getItem('team_id') || 0))
    const createdAt = currentTeam?.created_at
    const createdAtTime = isForceNewProV1() ? Date.now() : (createdAt ? Date.parse(createdAt) : Number.NaN)
    const plan = String(user.value?.sub_items_price?.plan?.item_billing_plan_type || 'free').toLowerCase()
    const residualTrialDays = user.value?.sub_items_price?.plan?.residual_trial_days || 0
    const isEnterprise = plan.includes('enterprise') || plan.includes('recapture')
    const isPro = isForceNewProV1() && !isEnterprise ? true : (plan.includes('pro') || plan.includes('premium') || residualTrialDays > 0)

    if (isEnterprise) {
      return '/attribution/web-analysis'
    }
    if (!isPro) {
      return '/attribution/explorer-web-analysis'
    }
    if (!Number.isFinite(createdAtTime)) {
      return '/attribution/web-analysis'
    }
    return createdAtTime >= webAnalysisV1LaunchAt.getTime()
      ? '/attribution/web-analysis-v1'
      : '/attribution/web-analysis'
  }

  const getDestinationsPath = (): string => {
    const destinationsV1LaunchAt = new Date('2026-03-25T00:00:00Z')
    const currentTeam = getCurrentTeam() || getTeamById(Number(sessionStorage.getItem('team_id') || 0))
    const createdAt = currentTeam?.created_at
    const createdAtTime = isForceNewProV1() ? Date.now() : (createdAt ? Date.parse(createdAt) : Number.NaN)
    const plan = String(user.value?.sub_items_price?.plan?.item_billing_plan_type || 'free').toLowerCase()
    const residualTrialDays = user.value?.sub_items_price?.plan?.residual_trial_days || 0
    const isEnterprise = plan.includes('enterprise') || plan.includes('recapture')
    const isPro = isForceNewProV1() && !isEnterprise ? true : (plan.includes('pro') || plan.includes('premium') || residualTrialDays > 0)

    if (isEnterprise || !isPro) {
      return '/conversion-feed/destinations'
    }
    if (!Number.isFinite(createdAtTime)) {
      return '/conversion-feed/destinations'
    }
    return createdAtTime >= destinationsV1LaunchAt.getTime()
      ? '/conversion-feed/destinations-v1'
      : '/conversion-feed/destinations'
  }

  // 登录状态检查
  const isLoggedIn = (): boolean => {
    return !!(token.value.accessToken || sessionStorage.getItem('token'))
  }

  // 清除用户信息
  const clearUserInfo = () => {
    user.value = {}
    token.value = {
      accessToken: '',
      refreshToken: '',
      refresh_token_valid_time: 0,
    }
  }

  // 完整登出功能
  const logout = () => {
    // 清除 store 状态
    clearUserInfo()

    isAppInitialized.value = false
    isInitializing.value = false

    // 清除 sessionStorage
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('team')
    sessionStorage.removeItem('team_id')
    sessionStorage.removeItem('team_code')
    sessionStorage.removeItem('user_id')
    sessionStorage.removeItem('user_email')
    sessionStorage.removeItem('user_name')
    sessionStorage.removeItem('teams')
    sessionStorage.removeItem('attribuly_init_app_cache')

    // 清除 localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('team')
    localStorage.removeItem('team_id')
    localStorage.removeItem('team_code')
    localStorage.removeItem('user_id')

    // 清除 cookies
    document.cookie = '_attribuly_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure'
    document.cookie = '_attribuly_team=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure'
  }

  // 处理 Self-site 登录或注册响应
  const handleSelfSiteLoginResponse = (response: {
    user: {
      id: number
      name: string
      team_id: number
      hash: string
    }
    teams: Array<Partial<Team>>
    token: string
    expires_in: number
  }, email?: string, domain?: string) => {
    // 设置 token
    setToken(response.token)
    sessionStorage.setItem('token', response.token)

    // 设置 cookie
    const expires = new Date(response.expires_in * 1000).toUTCString()
    document.cookie = `_attribuly_token=${response.token}; domain=.attribuly.com; path=/; expires=${expires}; SameSite=None; Secure`

    // 根据 response.user.team_id 找到对应的团队
    // 如果找不到,则使用第一个团队作为后备方案
    let currentTeam = response.teams.find(team => team.id === response.user.team_id)

    // 如果通过 team_id 没找到,且提供了 domain,尝试通过 domain 匹配
    if (!currentTeam && domain) {
      currentTeam = response.teams.find(team =>
        team.web_url && (team.web_url === domain || team.web_url === `/${domain}`),
      )
    }

    // 最后的后备方案：使用第一个团队
    if (!currentTeam && response.teams.length > 0) {
      currentTeam = response.teams[0]
      console.warn('无法匹配到指定的团队,使用第一个团队作为默认')
    }

    if (!currentTeam) {
      throw new Error('No team found in response')
    }

    // 构建完整的团队对象
    const team: Partial<Team> = {
      id: currentTeam.id,
      name: currentTeam.name,
      team_code: currentTeam.team_code,
      currency: currentTeam.currency || 'USD',
      timezone: currentTeam.timezone || 'Asia/Shanghai',
      web_url: currentTeam.web_url,
      conversion_window: 7, // 默认值
      default_model: '1cwm0j21fmwcyfwu07m624g100phdcbl', // 默认模型
      shop: currentTeam.shop || '',
      shop_type: 'self',
      webpixel_time: currentTeam.webpixel_time || '',
    }

    // 构建用户信息
    const userInfo: Partial<User> = {
      id: response.user.id,
      name: response.user.name,
      email: email || '', // 从请求参数中获取
      team_id: response.user.team_id,
      hash: response.user.hash,
      team: team as Team,
      team_name: currentTeam.name,
      team_timezone: team.timezone,
      team_list: response.teams.map(t => (t as Team)),
    }

    // 更新用户信息
    setUserInfo(userInfo)

    // 保存到 sessionStorage
    saveUserBasicInfo(userInfo, response.token)

    // 保存到 localStorage
    saveUserToLocalStorage(userInfo, response.user.team_id)

    // 设置团队 cookie
    document.cookie = `_attribuly_team=${JSON.stringify(team)}; domain=.attribuly.com; path=/; expires=${expires}; SameSite=None; Secure`

    return userInfo
  }

  // 处理 WooCommerce 登录或注册响应
  const handleWooCommerceLoginResponse = (response: {
    user: {
      id: number
      name: string
      team_id: number
      hash: string
    }
    teams: Array<Partial<Team>>
    token: string
    expires_in: number
  }, email?: string, domain?: string) => {
    // 设置 token
    setToken(response.token)
    sessionStorage.setItem('token', response.token)

    // 设置 cookie
    const expires = new Date(response.expires_in * 1000).toUTCString()
    document.cookie = `_attribuly_token=${response.token}; domain=.attribuly.com; path=/; expires=${expires}; SameSite=None; Secure`

    // 根据 response.user.team_id 找到对应的团队
    let currentTeam = response.teams.find(team => team.id === response.user.team_id)

    // 如果通过 team_id 没找到,且提供了 domain,尝试通过 domain 匹配
    if (!currentTeam && domain) {
      currentTeam = response.teams.find(team =>
        team.web_url && (team.web_url === domain || team.web_url === `/${domain}`),
      )
    }

    // 最后的后备方案：使用第一个团队
    if (!currentTeam && response.teams.length > 0) {
      currentTeam = response.teams[0]
      console.warn('无法匹配到指定的团队,使用第一个团队作为默认')
    }

    if (!currentTeam) {
      throw new Error('No team found in response')
    }

    // 构建完整的团队对象 (WooCommerce shop_type)
    const team: Partial<Team> = {
      id: currentTeam.id,
      name: currentTeam.name,
      team_code: currentTeam.team_code,
      currency: currentTeam.currency || 'USD',
      timezone: currentTeam.timezone || 'UTC',
      web_url: currentTeam.web_url,
      conversion_window: 7, // 默认值
      default_model: '1cwm0j21fmwcyfwu07m624g100phdcbl', // 默认模型
      shop: currentTeam.shop || '',
      shop_type: 'woocommerce',
      webpixel_time: currentTeam.webpixel_time || '',
    }

    // 构建用户信息
    const userInfo: Partial<User> = {
      id: response.user.id,
      name: response.user.name,
      email: email || '',
      team_id: response.user.team_id,
      hash: response.user.hash,
      team: team as Team,
      team_name: currentTeam.name,
      team_timezone: team.timezone,
      team_list: response.teams.map(t => (t as Team)),
    }

    // 更新用户信息
    setUserInfo(userInfo)

    // 保存到 sessionStorage
    saveUserBasicInfo(userInfo, response.token)

    // 保存到 localStorage
    saveUserToLocalStorage(userInfo, response.user.team_id)

    // 设置团队 cookie
    document.cookie = `_attribuly_team=${JSON.stringify(team)}; domain=.attribuly.com; path=/; expires=${expires}; SameSite=None; Secure`

    return userInfo
  }

  // 获取当前归因模型（conversion window）
  const getCurrentAttributionModel = async () => {
    try {
      const currentAttributionModelMutation = useCurrentAttributionModel()
      const res = await currentAttributionModelMutation.mutateAsync()

      if (res && res.conversion_window !== undefined) {
        // 更新当前团队的 conversion_window
        if (user.value.team) {
          user.value.team.conversion_window = res.conversion_window
        }

        // 更新团队列表中对应团队的 conversion_window
        if (user.value.team_list && user.value.team) {
          user.value.team_list = user.value.team_list.map((team) => {
            if (team.id === user.value.team?.id) {
              return { ...team, conversion_window: res.conversion_window }
            }
            return team
          })
        }

        return res.conversion_window
      }
    }
    catch (error) {
      console.error('获取 conversion window 失败:', error)
      return null
    }
  }

  // Store management
  // const currentStore = ref<any>(null)

  // const setCurrentStore = (store: any) => {
  //   currentStore.value = store
  //   // Save to session storage
  //   sessionStorage.setItem('currentStore', JSON.stringify(store))
  // }

  // const getCurrentStore = () => {
  //   if (currentStore.value) {
  //     return currentStore.value
  //   }
  //   // Try to load from session storage
  //   const stored = sessionStorage.getItem('currentStore')
  //   if (stored) {
  //     try {
  //       currentStore.value = JSON.parse(stored)
  //       return currentStore.value
  //     }
  //     catch (error) {
  //       console.error('Error parsing stored current store:', error)
  //     }
  //   }
  //   return null
  // }

  // const clearCurrentStore = () => {
  //   currentStore.value = null
  //   sessionStorage.removeItem('currentStore')
  // // Computed getters - 参考老系统的getters逻辑
  const isEventSubscription = (): boolean => {
    // 判断item_plan_type是否包含字符串'event_'
    const itemPlanType = user.value?.sub_items_price?.plan?.item_plan_type || ''
    return itemPlanType.includes('event_') || itemPlanType.includes('recapture_')
  }

  const isLast12MonthGmvSubscription = (): boolean => {
    // 判断item_plan_type是否包含字符串'event_'
    const itemPlanType = user.value?.sub_items_price?.plan?.item_billing_plan_type || ''
    return itemPlanType.includes('year_gmv_') || itemPlanType.includes('recapture_')
  }

  const isRecaptureSubscription = (): boolean => {
    const itemPlanType = user.value?.sub_items_price?.plan?.item_plan_type || ''
    return itemPlanType == 'recapture'
  }

  const isRecaptureBillingUser = (): boolean => {
    const itemPlanType = String(user.value?.sub_items_price?.plan?.item_plan_type || '').toLowerCase()
    return itemPlanType === 'recapture' || itemPlanType === 'recapture_free'
  }

  const isVersion4 = (): boolean => {
    return user.value?.event_type_v4 === true
  }

  const subscriptionPlan = computed((): string | undefined => {
    // 允许通过环境变量强制指定 Plan，用于开发调试
    /* const forcePlan = import.meta.env.VITE_FORCE_PLAN
    if (forcePlan && typeof forcePlan === 'string' && forcePlan.toLowerCase() === 'free') {
      return 'free'
    } */
    const plan = user.value?.sub_items_price?.plan?.item_billing_plan_type || 'free'
    if (plan.includes('enterprise') || plan.includes('recapture'))
      return 'enterprise'
    if (plan.includes('pro'))
      return 'pro'
    return 'free'
  })

  const isFree = (): boolean => {
    return user.value?.sub_items_price?.plan?.item_plan === 'free'
  }

  // 判断用户是否曾经订阅过 Pro/Premium（根据 subscription_sign 判断）
  const hadProBefore = (): boolean => {
    // 直接根据 subscription_sign 判断是否曾经订阅过
    const subscriptionSign = user.value?.sub_items_price?.first_subscription_sign
    return !subscriptionSign
  }

  const getTeamCurrency = (): string => {
    // 这里可以根据需要扩展货币逻辑
    return user.value?.team?.currency || 'USD'
  }

  // 开发辅助：将计划设为 Pro
  const setPlanPro = (): void => {
    const u: any = (user.value ||= {})
    // 安全初始化对象结构，避免 TS 下 "possibly undefined"
    u.sub_items_price = u.sub_items_price ?? {}
    u.sub_items_price.plan = u.sub_items_price.plan ?? {}

    // 设置为 Pro 计划
    u.sub_items_price.plan.item_plan = 'pro'
    // 关闭试用天数显示
    u.sub_items_price.plan.residual_trial_days = 0
    // 允许访问受限功能
    u.sub_items_price.access_flag = true
  }

  // 开发辅助：将计划设为 Pro Trial
  const setPlanProTrial = (): void => {
    const u: any = (user.value ||= {})
    // 安全初始化对象结构
    u.sub_items_price = u.sub_items_price ?? {}
    u.sub_items_price.plan = u.sub_items_price.plan ?? {}

    // 设置为 Pro 计划
    u.sub_items_price.plan.item_plan = 'pro'
    // 设置试用天数 > 0
    u.sub_items_price.plan.residual_trial_days = 14

    // 确保是 event subscription，以便显示 CreditsPanel
    if (!u.sub_items_price.plan.item_plan_type?.includes('event_')) {
      u.sub_items_price.plan.item_plan_type = 'event_subscription'
    }

    // 允许访问受限功能
    u.sub_items_price.access_flag = true
  }

  // 开发辅助：设置 Pro 计划的使用量百分比
  const setPlanProUsage = (percentage: number): void => {
    const u: any = (user.value ||= {})
    // 安全初始化对象结构
    u.sub_items_price = u.sub_items_price ?? {}
    u.sub_items_price.plan = u.sub_items_price.plan ?? {}

    // 设置为 Pro 计划
    u.sub_items_price.plan.item_plan = 'pro'
    // 确保是 event subscription
    u.sub_items_price.plan.item_plan_type = 'event_subscription'
    // 关闭试用
    u.sub_items_price.plan.residual_trial_days = 0
    // 允许访问受限功能
    u.sub_items_price.access_flag = true

    const quota = 10000
    const usage = Math.floor(quota * (percentage / 100))

    u.sub_items_price.plan.event_charge = {
      recurring_quota: quota,
      extra_quota: 0,
      usage: Math.min(usage, quota),
      pay_as_you_go_events: usage > quota ? usage - quota : 0,
    }
  }

  // 获取任务完成进度
  const getTaskCompleteProgress = (): number => {
    return user.value?.taskCompleteProgress || 0
  }

  // 统一订阅跳转方法：根据用户计划状态决定使用 Shopify API 还是直接跳转
  // 返回值：{ success: boolean, url?: string, error?: string }
  const handleSubscriptionUpgrade = async (): Promise<{ success: boolean, url?: string | null, error?: string }> => {
    const subscriptionUrl = getSubscriptionPlanUrl()

    try {
      // 判断是否为 Free 用户
      const isFreePlan = isFree()

      if (isFreePlan && !hadProBefore()) {
        // Free 用户：调用 Shopify 订阅接口获取链接并自动打开
        const response = await createShopifySubscriptionLink({
          sub_items: 'pro',
          sub_pro: {
            type: 'year',
          },
        })

        const payUrl = response?.data?.pay_url ?? null
        if (payUrl) {
          window.open(payUrl, '_blank', 'noopener')
          return { success: true, url: payUrl }
        }
        else {
          return { success: false, error: 'Failed to get subscription link from API' }
        }
      }
      else {
        // 非 Free 用户：直接跳转到订阅页面
        window.open(subscriptionUrl, '_blank', 'noopener,noreferrer')
        return { success: true, url: subscriptionUrl }
      }
    }
    catch (error: any) {
      console.error('订阅升级失败:', error)
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  return {
    // State
    user,
    token,
    // currentStore,
    isAppInitialized,
    isInitializing,

    // Actions
    setUserInfo,
    setDefaultModel,
    setToken,
    clearUserInfo,
    logout,
    handleSelfSiteLoginResponse,
    handleWooCommerceLoginResponse,

    // Methods
    initTokenAndSession,
    switchTeam,
    getQuickProAnnualSubscriptionLink,
    handleSubscriptionUpgrade,
    initApp,
    getCurrentAttributionModel,
    resetSwitchingState,
    restoreUserFromLocalStorage,
    checkLoginStatus,

    // Storage helpers
    saveUserToLocalStorage,
    saveUserBasicInfo,

    // Store management
    // setCurrentStore,
    // getCurrentStore,
    // clearCurrentStore,
    // Getters - 参考老系统的getters逻辑
    subscriptionPlan,
    isEventSubscription,
    isVersion4,
    isFree,
    hadProBefore,
    getTeamCurrency,

    // Task progress helpers
    calculateTaskCompleteProgress,
    updateTaskCompleteProgress,
    getTaskCompleteProgress,
    initOnboarding,

    // Helpers
    getCurrentTeam,
    getTeamById,
    hasAccessFlag,
    isOnboardingFinished,
    getServerTime,
    getBillingPath,
    getSegmentsPath,
    getWebAnalysisPath,
    getDestinationsPath,
    getSubscriptionPlanUrl,
    shouldUseSidebarMultiModulePlanStatusCard,
    isLoggedIn,
    // Dev helpers
    setPlanPro,
    setPlanProTrial,
    setPlanProUsage,
    isLast12MonthGmvSubscription,
    isRecaptureSubscription,
    isRecaptureBillingUser,
  }
})
